// src/utils/imageSpec.ts
//
// Single source of truth for parsing & normalizing image specs.
// Handles strings like:
//   "ror.png { position: top-left, anim: fade-in, delay: 0.8, dur: 3000ms }"
//   ror.png
//   { image: "ror.png", position: "top-left" }
// Also heals common YAML/front-matter "shattered" cases and stray quotes.
// Converts delay/dur to seconds, dist to pixels (numbers), and normalizes position tokens.

import { parseInlineAttrs } from "@/markdown/parseInlineAttrs";

/** Public types */
export type ImagePointer = "auto" | "none";

export type ImageSpecInput =
  | string
  | {
      image?: string; // preferred
      src?: string;   // alias
      alt?: string;
      position?: string;
      corner?: string; // alias
      pos?: string;    // alias
      anchor?: string; // alias

      href?: string;
      link?: string; // alias for href
      target?: "_blank" | "_self" | "_parent" | "_top";

      class?: string;
      className?: string; // alias
      z?: number | string;
      pointer?: ImagePointer;

      width?: number | string;
      height?: number | string;

      anim?: string;

      // timing/anim extras (accept both raw seconds and "300ms")
      delay?: number | string;
      start?: number | string; // alias
      at?: number | string;    // alias
      wait?: number | string;  // alias

      dur?: number | string;
      duration?: number | string; // alias

      dist?: number | string;

      // precise positioning
      x?: number | string;   // absolute left (px/%)
      y?: number | string;   // absolute top  (px/%)
      dx?: number | string;  // offset X from anchor (px/%)
      dy?: number | string;  // offset Y from anchor (px/%)
    };

export interface NormalizedImageSpec {
  /** Raw filename or absolute/hosted path exactly as authored (quotes removed, HTML entities decoded) */
  src: string;

  /** Normalized position token (e.g., "top-left", "center", "bottom-right") */
  position?: string;

  /** Values as authored (not coerced). */
  alt?: string;
  href?: string;
  target?: "_blank" | "_self" | "_parent" | "_top";
  class?: string;
  z?: number | string;
  pointer?: ImagePointer;
  width?: number | string;
  height?: number | string;
  anim?: string;

  /** Canonical timing/anim numbers for engine consumption */
  delaySec?: number; // undefined if missing/invalid
  durSec?: number;   // undefined if missing/invalid
  distPx?: number;   // undefined if missing/invalid

  /** Precise placement (CSS-ready lengths: "250px", "50%") */
  x?: string;  // absolute left
  y?: string;  // absolute top
  dx?: string; // offset X (applied via translateX)
  dy?: string; // offset Y (applied via translateY)

  /** Original raw fields if you still want them */
  _raw?: Record<string, any>;
}
/** Return a CSS length string from number or "12", "12px", "40%". Bare numbers -> "px". */
export function cssLength(v: unknown): string | undefined {
  if (v == null || v === "") return undefined;
  if (typeof v === "number" && Number.isFinite(v)) return `${v}px`;
  const s = String(v).trim().toLowerCase();
  if (!s) return undefined;
  if (/^[+-]?\d+(\.\d+)?%$/.test(s)) return s;
  if (/^[+-]?\d+(\.\d+)?px$/.test(s)) return s;
  const n = Number(s);
  if (Number.isFinite(n)) return `${n}px`;
  return undefined;
}

/* --------------------------------------------------------- */
/* Utilities (exported where it helps other code reuse them)  */
/* --------------------------------------------------------- */

export function stripOuterQuotes(s: string): string {
  return s.replace(/^['"]|['"]$/g, "");
}

export function decodeHtmlEntities(s: string): string {
  // keep it small & safe; we only need a few common ones we saw (&quot;)
  return s
    .replace(/&quot;|&#34;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

/** Normalize author tokens like "Top Left", "tl", "centre" → canonical forms. */
export function normalizePositionToken(raw?: unknown): string | undefined {
  if (raw == null) return undefined;
  const t = String(raw)
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\-]/g, ""); // drop stray punctuation

  if (!t) return undefined;

  const MAP: Record<string, string> = {
    // corners
    "top-left": "top-left", "topleft": "top-left", "tl": "top-left",
    "top-right": "top-right", "topright": "top-right", "tr": "top-right",
    "bottom-left": "bottom-left", "bottomleft": "bottom-left", "bl": "bottom-left",
    "bottom-right": "bottom-right", "bottomright": "bottom-right", "br": "bottom-right",

    // edges + centers
    "top-center": "top-center", "tc": "top-center", "top": "top-center",
    // additional synonyms
    "center-top": "top-center", "centre-top": "top-center", "middle-top": "top-center", "top-middle": "top-center",
    "bottom-center": "bottom-center", "bc": "bottom-center", "bottom": "bottom-center",
    "center-bottom": "bottom-center", "centre-bottom": "bottom-center", "middle-bottom": "bottom-center", "bottom-middle": "bottom-center",

    // canonicalize to center-* (not middle-*)
    "center-left": "center-left", "centre-left": "center-left", "middle-left": "center-left", "ml": "center-left",
    "center-right": "center-right", "centre-right": "center-right", "middle-right": "center-right", "mr": "center-right",

    "center": "center", "centre": "center", "middle": "center", "cc": "center",

    // single-axis shorthands
    "left": "center-left",
    "right": "center-right",
  };

  return MAP[t] || t; // if unfamiliar, return the cleaned token (caller can decide default)
}

/** Parse seconds from number, "0.3", "300ms", or "0.3s". Returns undefined if invalid. */
export function seconds(v: unknown): number | undefined {
  if (v == null || v === "") return undefined;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const s = String(v).trim().toLowerCase();
  if (/^\d+(\.\d+)?ms$/.test(s)) {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n / 1000 : undefined;
  }
  if (/^\d+(\.\d+)?s$/.test(s)) {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : undefined;
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

/** Parse pixels from number, "24", or "24px". Returns undefined if invalid. */
export function pixels(v: unknown): number | undefined {
  if (v == null || v === "") return undefined;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const s = String(v).trim().toLowerCase();
  if (/^\d+(\.\d+)?px$/.test(s)) {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : undefined;
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

/* --------------------------------------------------------- */
/* Healing for shattered front-matter                        */
/* --------------------------------------------------------- */

/**
 * If a section attrs object split "image: 'foo.png { ...'" from trailing keys
 * (position, delay, etc.), rebuild a single string so downstream parsing sees it whole.
 * Mutates attrs in-place.
 */
export function healShatteredImageSpec(attrs: Record<string, any>): void {
  if (!attrs || typeof attrs.image !== "string") return;

  const raw = stripOuterQuotes(decodeHtmlEntities(attrs.image.trim()));
  const hasOpen = raw.includes("{");
  const hasClose = /}\s*$/.test(raw);

  // If already properly closed, nothing to heal.
  if (hasClose) return;

  // Derive filename/body whether or not a brace exists yet.
  let filename: string;
  let body: string;
  if (hasOpen) {
    const braceIdx = raw.indexOf("{");
    filename = raw.slice(0, braceIdx).trim();
    body = raw.slice(braceIdx + 1).trim(); // without leading "{"
  } else {
    // Fabricate an empty body so we can append picked keys into braces.
    filename = raw;
    body = "";
  }

  const KNOWN = [
    "position","corner","pos","anchor",
    "anim","delay","dur","duration","dist",
    "class","z","pointer",
    "href","link","target",
    "width","height","alt",
    "start","at","wait",
    // precise positioning
    "x","y","dx","dy",
  ];

  const picked: string[] = [];
  for (const k of KNOWN) {
    if (attrs[k] == null) continue;
    const v = String(attrs[k]).trim();
    if (v === "") continue;
    // move it into the brace body (quotes are okay; parseInlineAttrs can handle)
    picked.push(`${k}: ${v}`);
  }

  body = body.replace(/}\s*$/g, "").trim();
  const combined = [body, ...picked].filter(Boolean).join(", ").replace(/\s+,/g, ",");
  attrs.image = `${filename} { ${combined} }`;

  // Clean up moved keys so we don't double-parse later
  for (const k of KNOWN) delete attrs[k];
}

/* --------------------------------------------------------- */
/* Core parsers                                              */
/* --------------------------------------------------------- */

/**
 * Parse a single string token which may look like:
 *   "file.png { key: value, ... }"
 *   file.png
 *   { image: "file.png", position: top-left }
 */
export function parseImageToken(token: string): ImageSpecInput {
  const raw0 = token.trim();
  const raw = stripOuterQuotes(decodeHtmlEntities(raw0));

  // Case 1: starts with a brace → let the inline parser handle it
  if (raw.startsWith("{") && raw.endsWith("}")) {
    const obj = (parseInlineAttrs(raw) as unknown) as Record<string, any>;
    if (obj && (obj.image || obj.src)) return obj as ImageSpecInput;
  }

  // Case 2: filename + brace blob
  const braceIdx = raw.indexOf("{");
  if (braceIdx > 0) {
    const filename = raw.slice(0, braceIdx).trim();
    let brace = raw.slice(braceIdx).trim();
    if (!brace.endsWith("}")) brace = brace + "}"; // be lenient
    let extra: Record<string, any> = {};
    try {
      extra = (parseInlineAttrs(brace) as unknown) as Record<string, any>;
    } catch {
      extra = {};
    }
    return { image: filename, ...extra } as ImageSpecInput;
  }

  // Case 3: just filename (or inline-attrs-like string)
  // Try inline attrs first to allow "image: foo.png" cases
  try {
    const obj = (parseInlineAttrs(raw) as unknown) as Record<string, any>;
    if (obj && (obj.image || obj.src)) {
      return obj as ImageSpecInput;
    }
  } catch { /* ignore */ }

  // Fallback: treat as bare filename
  return { image: raw } as ImageSpecInput;
}

/**
 * Normalize *any* authored input (string or object) into a single NormalizedImageSpec.
 * - Strips stray quotes & decodes &quot; etc.
 * - Picks image/src
 * - Resolves position (position|corner|pos) → canonical token
 * - Coerces delay/start/at/wait → delaySec, dur/duration → durSec, dist → distPx
 */
export function parseImageSpec(input: ImageSpecInput): NormalizedImageSpec {
  const base: Record<string, any> =
    typeof input === "string" ? (parseImageToken(input) as any) : { ...(input as any) };

  // pick image/src, strip quotes/entities
  const srcRaw = base.image ?? base.src ?? "";
  const src = stripOuterQuotes(decodeHtmlEntities(String(srcRaw).trim()));

  // pixel/percent coordinates & offsets (for precise placement)
  const posStr = base.position ?? base.corner ?? base.pos ?? base.anchor;
  let xCss = cssLength(base.x);
  let yCss = cssLength(base.y);
  let position: string | undefined;

  // If author used a "x y" pair inside `position`, treat as precise coords.
  if (!xCss && !yCss && typeof posStr === "string") {
    const m = posStr.trim().match(/^([+-]?[0-9.]+(?:px|%)?)\s+([+-]?[0-9.]+(?:px|%)?)$/);
    if (m) {
      xCss = cssLength(m[1]);
      yCss = cssLength(m[2]);
    }
  }

  // Only compute an anchor token if we did NOT resolve any precise coordinates.
  // This ensures renderers enter "precise mode" whenever x/y are present.
  if (!xCss && !yCss) {
    position = normalizePositionToken(posStr) || undefined;
  }

  const dxCss = cssLength(base.dx);
  const dyCss = cssLength(base.dy);

  // href/link normalization (strip quotes & decode entities)
  const hrefRaw = base.href ?? base.link;
  const href = hrefRaw != null
    ? stripOuterQuotes(decodeHtmlEntities(String(hrefRaw).trim()))
    : undefined;

  // time/distance coercion
  // Preserve already-computed seconds if present on the input, otherwise coerce aliases.
  const delayRaw = base.delay ?? base.start ?? base.at ?? base.wait;
  const durRaw   = base.dur ?? base.duration;

  const delaySec =
    (typeof base.delaySec === "number" ? base.delaySec : seconds(base.delaySec)) ??
    seconds(delayRaw);

  const durSec =
    (typeof base.durSec === "number" ? base.durSec : seconds(base.durSec)) ??
    seconds(durRaw);

  const distPx = pixels(base.dist);

  const normalized: NormalizedImageSpec = {
    src,
    position,
    alt: base.alt,
    href,
    target: base.target,
    class: base.class ?? base.className,
    z: base.z,
    pointer: base.pointer as ImagePointer | undefined,
    width: base.width,
    height: base.height,
    anim: base.anim,
    delaySec,
    durSec,
    distPx,
    x: xCss,
    y: yCss,
    dx: dxCss,
    dy: dyCss,
    _raw: base,
  };

  return normalized;
}

/**
 * Convenience: take a mixed list (string/object) and return normalized specs.
 */
export function parseImageSpecList(list: Array<ImageSpecInput>): NormalizedImageSpec[] {
  const out: NormalizedImageSpec[] = [];
  for (const item of list) {
    try {
      const spec = parseImageSpec(item);
      if (!spec.src) continue;
      out.push(spec);
    } catch (e) {
      console.warn("[imageSpec] failed to parse item:", item, e);
    }
  }
  return out;
}